import { useEffect, useState } from 'react';
import useSearchParams from '../../../../../../../hooks/useSearchParams';
import { toggleSetValue } from '../../../../../../../utils/set.utils';
import {
  ORDER_TARGETS_TAB_EXPAND_PARAM,
  getExpandTargetParam,
} from '../../../../../utils/orderTargetsTab.utils';
import { ToggleOrderTargetRowParams } from '../orderObjectTargetsTab.types';

const toExpandedIds = (targetId: string | null) => new Set(targetId ? [targetId] : []);

const useOrderTargetsExpandedIds = () => {
  const { searchParams, removeSearchParams } = useSearchParams();
  const targetIdToExpand = getExpandTargetParam(searchParams);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    toExpandedIds(targetIdToExpand),
  );
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  const [isKeepingTrees, setIsKeepingTrees] = useState(false);
  const [itemTargetIds, setItemTargetIds] = useState<Map<string, string>>(new Map());
  const [appliedTargetId, setAppliedTargetId] = useState(targetIdToExpand);

  if (targetIdToExpand !== appliedTargetId) {
    setAppliedTargetId(targetIdToExpand);

    if (targetIdToExpand) {
      setIsAllExpanded(false);
      setIsKeepingTrees(false);
      setExpandedIds(toExpandedIds(targetIdToExpand));
    }
  }

  useEffect(() => {
    if (getExpandTargetParam(searchParams)) {
      removeSearchParams([ORDER_TARGETS_TAB_EXPAND_PARAM]);
    }
  }, [searchParams, removeSearchParams]);

  const changeExpansion = (isExpanded: boolean) => {
    setIsAllExpanded(isExpanded);
    setIsKeepingTrees(false);
    setExpandedIds(new Set());
  };

  const toggle = ({ rowId, targetId, visibleExpandedIds }: ToggleOrderTargetRowParams) => {
    setIsKeepingTrees((current) => current || isAllExpanded);
    setIsAllExpanded(false);
    setExpandedIds(toggleSetValue(visibleExpandedIds, rowId));

    if (rowId !== targetId) {
      setItemTargetIds((current) => new Map(current).set(rowId, targetId));
    }
  };

  return {
    expandedIds,
    isAllExpanded,
    isUsingFullTrees: isAllExpanded || isKeepingTrees,
    itemTargetIds,
    changeExpansion,
    toggle,
  };
};

export default useOrderTargetsExpandedIds;
